import { Button } from '@design/components/button/Button';
import { Drawer } from '@design/components/drawer/Drawer';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { DrawerContentWrapper } from '@terminal/components/drawer-content-wrapper/DrawerContentWrapper';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { t } from 'i18next';

import styles from './AssetOverviewDrawer.module.scss';
import { AssetOverviewWidget } from './AssetOverviewWidget';

export interface AssetOverviewDrawerProps {
  entityTag: string;
  closeDrawer: () => void;
  isOpen: boolean;
}

export const AssetOverviewDrawer = ({
  entityTag,
  closeDrawer,
  isOpen,
}: AssetOverviewDrawerProps) => {
  const {
    data,
    isLoading: entityLoading,
    isError: entityError,
  } = useEntity(entityTag);
  const { navigateToPage } = useNavigateToAssetOverview();

  const navigateToAssetOverview = () => {
    navigateToPage({
      entityTag,
      source: POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW_DRAWER,
    });
  };

  const entity = data?.data;

  return (
    <Drawer
      data-testid="asset-overview-drawer"
      isOpen={isOpen}
      handleCloseClick={closeDrawer}
      headerButton={
        <WebOnlyFeat>
          <Button
            iconName="Expand"
            label={t('widget:assetOverview.assetOverview')}
            onClick={navigateToAssetOverview}
            variant="empty"
            size="xsmall"
          />
        </WebOnlyFeat>
      }
    >
      <DrawerContentWrapper>
        {entityLoading ? (
          <SkeletonLoader />
        ) : entityError || !entity ? (
          <div className={styles.errorMessageWrapper}>
            <ErrorMessageWrapper variant="empty" displayIcon />
          </div>
        ) : (
          <AssetOverviewWidget
            entity={entity}
            isDrawer
            source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW_DRAWER}
          />
        )}
      </DrawerContentWrapper>
    </Drawer>
  );
};
